import React, { useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { AutocompleteField } from "../../shared/inputs/AutocompleteField";
import { PrimaryButton } from "../../shared/buttons/PrimaryButton.styled";
import { SecondaryButton } from "../../shared/buttons/SecondaryButton.styled";
import { BodyText } from "../../shared/text/BodyText.styled";
import { HeaderText } from "../../shared/text/HeaderText.styled";
import { ediphiPalette } from "../../styles/colors";
import { CURRENCIES, type Currency, type CurrencyScope } from "../../../excel/userTools/currency";
import { CHANGE_CURRENCY_DESCRIPTION, CHANGE_CURRENCY_TITLE } from "../../constants";

interface CurrencyDialogProps {
  activeSheetName: string;
  onSubmit: (currency: Currency, scope: CurrencyScope) => void;
  onClose: () => void;
}

/**
 * Picks the currency to switch to and whether it applies to every sheet or only the active one.
 * Opens with no currency selected and remembers nothing between openings.
 */
const CurrencyDialog: React.FC<CurrencyDialogProps> = ({ activeSheetName, onSubmit, onClose }) => {
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [scope, setScope] = useState<CurrencyScope>("workbook");

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth sx={{ m: 2 }}>
      <DialogTitle sx={{ color: ediphiPalette.black950, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
        {CHANGE_CURRENCY_TITLE}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 2 }}>
        <BodyText sx={{ mb: 2 }}>{CHANGE_CURRENCY_DESCRIPTION}</BodyText>
        <AutocompleteField
          options={[...CURRENCIES]}
          value={currency}
          onChange={setCurrency}
          getOptionLabel={(option) => option.label}
          getOptionId={(option) => option.id}
          label="Currency"
        />
        <HeaderText sx={{ pt: 2, pb: "4px", fontSize: "12px" }}>Apply to</HeaderText>
        <RadioGroup value={scope} onChange={(event) => setScope(event.target.value as CurrencyScope)}>
          <FormControlLabel value="workbook" control={<Radio size="small" />} label="All sheets" />
          <FormControlLabel
            value="activeSheet"
            control={<Radio size="small" />}
            label={activeSheetName ? `Active sheet (${activeSheetName})` : "Active sheet"}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 24px 24px" }}>
        <SecondaryButton size="large" autoFocus onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton size="large" onClick={() => currency && onSubmit(currency, scope)} disabled={!currency}>
          Apply
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default CurrencyDialog;
